import React, {
  ReactElement,
  Ref,
  forwardRef,
  useContext,
  useEffect,
} from 'react';
import { useIntl } from 'react-intl';
import ButtonGroup from '@material-ui/core/ButtonGroup/ButtonGroup';
import Typography from '@material-ui/core/Typography';
import ControlPointIcon from '@material-ui/icons/ControlPoint';
import RemoveIcon from '@material-ui/icons/Delete';
import EditIcon from '@material-ui/icons/Edit';
import {
  ActionButtonComponentProps,
  ActionButtonHandle,
  DetailContext,
  DetailMode,
  RemoteTableFieldContext,
  TableFieldToolbarButton,
  TableFieldToolbarProps,
  UserContext,
} from '@eas/common-web';
import { DetailActionButton } from '@components/action-button/detail-action-button';
import { useStyles } from '@components/form/table-field/form-table-toolbar-styles';
import { Role } from '@enums';
import { useAddDraftDialog } from './dialog-actions/add-draft-hook';
import { useDeleteDraftDialog } from './dialog-actions/delete-draft-hook';
import { useEditDraftDialog } from './dialog-actions/edit-draft-hook';
import { DraftDialog } from './draft-dialog';
import { useValidationSchema } from './draft-schema';

export const DraftToolbar = forwardRef(function DraftToolbar(
  {
    selectedIndex,
    setSelectedIndex,
    disabled,
  }: TableFieldToolbarProps & { disabled: boolean },
  ref: Ref<ActionButtonHandle>
) {
  const classes = useStyles();

  const intl = useIntl();

  const { hasPermission, user } = useContext(UserContext);
  const { source: detailSource } = useContext(DetailContext);
  const { source: tableSource } = useContext(RemoteTableFieldContext);
  const drafts = tableSource.items;

  const validationSchema = useValidationSchema();

  const { callApi: addDraftApiCall } = useAddDraftDialog();
  const { callApi: editDraftApiCall } = useEditDraftDialog();
  const { callApi: deleteDraftApiCall } = useDeleteDraftDialog();

  /**
   * Disabled for users who are not ADMIN or did not create the draft
   */
  const hasPermissionToEdit =
    hasPermission(Role.ADMIN) ||
    (selectedIndex !== undefined &&
      tableSource.items?.[selectedIndex]?.createdBy?.id === user.id);

  const selectedDraft =
    selectedIndex !== undefined ? drafts?.[selectedIndex] : {};

  const isSelected = selectedIndex !== undefined;

  useEffect(() => {
    setSelectedIndex(undefined);
  }, [detailSource]);

  return (
    <div className={classes.tableActions}>
      <ButtonGroup className={classes.buttonGroup}>
        <DetailActionButton
          promptKey="ADD_DRAFT"
          apiCall={addDraftApiCall}
          onSuccess={async () => {
            requestAnimationFrame(() => {
              tableSource.reset();
              tableSource.loadMore();
              setSelectedIndex(undefined);
            });
          }}
          disabled={disabled}
          buttonLabel={intl.formatMessage({
            id: 'ES__DRAFTS_TOOLBAR__ADD_DRAFT',
            defaultMessage: 'Přidat koncept',
          })}
          dialogTitle={intl.formatMessage({
            id: 'ES__DIALOG_ADD_DRAFT__TITLE',
            defaultMessage: 'Vytvoření konceptu',
          })}
          dialogText={intl.formatMessage({
            id: 'ES__DIALOG_ADD_DRAFT__TEXT',
            defaultMessage: 'Vyplňte formulář.',
          })}
          ButtonComponent={({ label, onClick }) => (
            <TableFieldToolbarButton
              IconComponent={ControlPointIcon}
              show={true}
              disabled={disabled}
              title={label}
              onClick={onClick}
              color="primary"
              variant="contained"
            >
              <Typography variant="body2">
                {intl.formatMessage({
                  id: 'ES__DRAFTS_TOOLBAR__ADD_DRAFT__BUTTON',
                  defaultMessage: 'Přidat',
                })}
              </Typography>
            </TableFieldToolbarButton>
          )}
          FormFields={() => <DraftDialog />}
          modes={[DetailMode.VIEW, DetailMode.EDIT]}
          formValidationSchema={validationSchema}
          dialogWidth={700}
        />
        <DetailActionButton
          promptKey="EDIT_DRAFT"
          apiCall={(id, formData) =>
            editDraftApiCall(drafts![selectedIndex!].id, formData)
          }
          ref={ref}
          buttonLabel={intl.formatMessage({
            id: 'ES__DRAFTS_TOOLBAR__EDIT_DRAFT',
            defaultMessage: 'Editovat koncept',
          })}
          dialogTitle={intl.formatMessage({
            id: 'ES__DRAFTS_TOOLBAR__EDIT_DRAFT__TITLE',
            defaultMessage: 'Koncept',
          })}
          dialogText={intl.formatMessage({
            id: 'ES__DRAFTS_TOOLBAR__EDIT_DRAFT__TEXT',
            defaultMessage: 'Vyplňte formulář.',
          })}
          FormFields={() => <DraftDialog />}
          ButtonComponent={({ onClick, label }: ActionButtonComponentProps) => (
            <TableFieldToolbarButton
              IconComponent={EditIcon}
              show={true}
              disabled={
                disabled ||
                !isSelected ||
                !hasPermissionToEdit ||
                selectedDraft.finalVersion
              }
              title={label}
              onClick={onClick}
              color="default"
            >
              <Typography variant="body2">
                {intl.formatMessage({
                  id: 'ES__DRAFTS_TOOLBAR__EDIT_DRAFT__BUTTON',
                  defaultMessage: 'Upravit',
                })}
              </Typography>
            </TableFieldToolbarButton>
          )}
          formInitialValues={selectedDraft}
          modes={[DetailMode.VIEW, DetailMode.EDIT]}
          formValidationSchema={validationSchema}
          dialogWidth={700}
        />
        <DetailActionButton
          promptKey="DELETE_DRAFT"
          apiCall={() => deleteDraftApiCall(drafts![selectedIndex!].id)}
          buttonLabel={intl.formatMessage({
            id: 'ES__DRAFTS_TOOLBAR__DELETE_DRAFT',
            defaultMessage: 'Odstranit koncept',
          })}
          dialogTitle={intl.formatMessage({
            id: 'ES__DRAFTS_TOOLBAR__DELETE_DRAFT__TITLE',
            defaultMessage: 'Koncept',
          })}
          dialogText={intl.formatMessage({
            id: 'ES__DRAFTS_TOOLBAR__DELETE_DRAFT__TEXT',
            defaultMessage: 'Skutečně chcete odstranit koncept?',
          })}
          ButtonComponent={({ onClick, label }: ActionButtonComponentProps) => (
            <TableFieldToolbarButton
              IconComponent={RemoveIcon}
              show={true}
              disabled={
                disabled ||
                !isSelected ||
                !hasPermissionToEdit ||
                selectedDraft.finalVersion
              }
              title={label}
              onClick={onClick}
              color="secondary"
            >
              <Typography variant="body2">
                {intl.formatMessage({
                  id: 'ES__NOTES_TOOLBAR__DELETE_NOTE__BUTTON',
                  defaultMessage: 'Smazat',
                })}
              </Typography>
            </TableFieldToolbarButton>
          )}
          modes={[DetailMode.VIEW, DetailMode.EDIT]}
        />
      </ButtonGroup>
    </div>
  );
}) as (
  p: TableFieldToolbarProps & {
    ref: Ref<ActionButtonHandle>;
    disabled: boolean;
  }
) => ReactElement;
